/**
 * When are two dinners "the same meal"? (#736)
 *
 * A French cook was offered "Fish Tacos" on a Sunday and "Tacos poisson" the
 * next day. They are two catalogue recipes — NYT and La Presse, two ids, two
 * languages — and the generator only ever compared ids, so to it they were as
 * different as a curry and a pie. To the cook they are one dinner, served twice.
 *
 * Identity, strongest first:
 *
 *   1. same recipe — the same id;
 *   2. same title — the titles match once normalised, in any language the
 *      recipe carries a title in ("Tacos poisson" is filed with the English
 *      title "Fish Tacos", which is the other recipe's own title);
 *   3. same dish — the same dish format made with the same main protein
 *      (tacos + fish). Two recipes can share nothing on paper and still be
 *      this;
 *   4. same format — the same dish format with any protein (fish tacos, then
 *      chicken fajitas).
 *
 * Each has a window. 1 to 3 are the same meal, so they are two weeks apart at
 * least, looking back as well as forward. 4 is the same kind of dinner, so it
 * is once per seven days — one plan never carries two.
 *
 * A pure module: no store, no Firebase, so both generators and their tests
 * share it.
 */
import { PROTEIN_KEYWORDS, detectProtein, withoutSeafoodSeasoning } from '../utils/dietClassifier';
import { decodeHtmlEntities } from '../utils/stripInstructionHtml';
import { parseIsoDate } from '../utils/localDate';

/**
 * Dish formats, matched against the title only.
 *
 * Shared with the generator's older soft variety score (`detectDishFormat`),
 * which matches them as substrings; here they are anchored at word edges, so
 * "bolognese" is not a bowl ("bol") and "wokking" is not a stir-fry.
 */
export const DISH_FORMAT_KEYWORDS: Record<string, string[]> = {
  tacos: ['taco', 'tacos', 'fajita', 'fajitas'],
  soup: ['soup', 'soupe', 'potage', 'velouté', 'bisque', 'chowder'],
  pasta: ['pasta', 'pâtes', 'spaghetti', 'penne', 'linguine', 'fettuccine', 'lasagna', 'lasagne'],
  curry: ['curry', 'cari'],
  stir_fry: ['stir fry', 'stir-fry', 'sauté au wok', 'wok'],
  burger: ['burger', 'hamburger'],
  salad: ['salad', 'salade'],
  bowl: ['bowl', 'bol', 'poke', 'poké'],
  wrap: ['wrap', 'burrito', 'burritos'],
  stew: ['stew', 'ragoût', 'ragout', 'mijoté'],
  sandwich: ['sandwich', 'panini', 'grilled cheese'],
  pizza: ['pizza', 'flatbread'],
};

/** The rules, strongest first. */
export type SameMealRule = 'same-recipe' | 'same-title' | 'same-dish' | 'same-format';

/**
 * Minimum days between two dinners that match under each rule. Fourteen means
 * a Sunday's fish tacos come back on the Sunday two weeks later at the earliest.
 */
export const SAME_MEAL_WINDOW_DAYS: Record<SameMealRule, number> = {
  'same-recipe': 14,
  'same-title': 14,
  'same-dish': 14,
  'same-format': 7,
};

/** How far back a generator must look: the widest window. */
export const SAME_MEAL_LOOKBACK_DAYS = Math.max(...Object.values(SAME_MEAL_WINDOW_DAYS));

/**
 * The order rules are dropped in when honouring all of them would leave a slot
 * empty. Each tier is the set still enforced; the last one — the same recipe,
 * or its twin under another title — is never given up.
 */
export const SAME_MEAL_TIERS: SameMealRule[][] = [
  ['same-recipe', 'same-title', 'same-dish', 'same-format'],
  ['same-recipe', 'same-title', 'same-dish'],
  ['same-recipe', 'same-title'],
];

export interface MealIdentity {
  recipeId: string;
  /** Normalised titles, one per language the recipe has a title in. */
  titleKeys: string[];
  format: string | null;
  protein: string | null;
}

/** What a recipe needs to have for its identity to be read. */
export interface IdentifiableRecipe {
  id: string | number;
  title: string;
  translatedTitles?: { en?: string; fr?: string };
  /** Ingredient lines — `PersonalRecipeIndexEntry.ingredients`. */
  ingredients?: string[];
  /** Full-recipe shape, as a planned meal carries it. */
  extendedIngredients?: Array<{ original?: string; name?: string }>;
}

/**
 * A recipe's ingredient lines, whichever shape carries them — an index entry's
 * `ingredients` or a planned meal's `extendedIngredients`. Documents read on
 * the server can carry a legacy `ingredients` shape (objects, not lines), so
 * only strings are trusted from either field.
 */
export function ingredientLinesOf(recipe: Pick<IdentifiableRecipe, 'ingredients' | 'extendedIngredients'>): string[] {
  const lines = (recipe.ingredients ?? []).filter((l): l is string => typeof l === 'string');
  if (lines.length > 0) return lines;
  return (Array.isArray(recipe.extendedIngredients) ? recipe.extendedIngredients : [])
    .map((i) => (typeof i?.original === 'string' && i.original) || (typeof i?.name === 'string' && i.name) || '');
}

// Words that carry no identity: articles, linking words, and the adjectives
// every second recipe puts in its title.
const STOP_WORDS = new Set([
  'a', 'an', 'the', 'and', 'with', 'of', 'in', 'on', 'for', 'or', 'style',
  'de', 'du', 'des', 'la', 'le', 'les', 'l', 'd', 'au', 'aux', 'et', 'en', 'avec',
  'sur', 'pour', 'un', 'une', 'ou', 'facon',
  'easy', 'simple', 'quick', 'best', 'homemade', 'recipe',
  'facile', 'rapide', 'maison', 'recette',
]);

/**
 * A title reduced to what identifies the dish: accents, case, punctuation,
 * linking words and plurals gone, words sorted. "Tacos de poisson" and
 * "Poisson (tacos)" read the same; "Fish Tacos" and "Tacos poisson" do not —
 * they are different languages, which is what `translatedTitles` is for.
 */
export function normalizeTitleKey(title: string): string {
  const words = decodeHtmlEntities(title)
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter((w) => w && !STOP_WORDS.has(w))
    .map((w) => (w.length > 3 && w.endsWith('s') ? w.slice(0, -1) : w));
  return [...new Set(words)].sort().join(' ');
}

function keywordRegex(keyword: string): RegExp {
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(?<![\\p{L}\\p{N}])${escaped}(?:s|x|es)?(?![\\p{L}\\p{N}])`, 'iu');
}

const FORMAT_MATCHERS = Object.entries(DISH_FORMAT_KEYWORDS).map(
  ([format, keywords]) => [format, keywords.map(keywordRegex)] as const,
);
const PROTEIN_MATCHERS = Object.entries(PROTEIN_KEYWORDS).map(
  ([protein, keywords]) => [protein, keywords.map(keywordRegex)] as const,
);

/** The dish format a title names, or null. */
export function dishFormatOf(title: string): string | null {
  const text = decodeHtmlEntities(title);
  for (const [format, matchers] of FORMAT_MATCHERS) {
    if (matchers.some((m) => m.test(text))) return format;
  }
  return null;
}

/**
 * The protein the dish is about.
 *
 * The title decides when it names one, and the one it names first wins:
 * "Plaque de crevettes, haricots noirs et chorizo" is a shrimp dish, even
 * though `detectProtein` checks pork before seafood. Only a title that names
 * none falls back to the ingredients.
 */
export function mainProteinOf(title: string, ingredients: string[]): string | null {
  const text = withoutSeafoodSeasoning(decodeHtmlEntities(title));
  let best: { protein: string; at: number } | null = null;
  for (const [protein, matchers] of PROTEIN_MATCHERS) {
    for (const m of matchers) {
      const hit = m.exec(text);
      if (hit && (best === null || hit.index < best.at)) best = { protein, at: hit.index };
    }
  }
  if (best) return best.protein;
  return detectProtein({ title: '', ingredients });
}

const identityCache = new WeakMap<object, MealIdentity>();
/**
 * The same identity by content, for a recipe object that was recreated (#865):
 * every live-sync snapshot rebuilds the planned meals and their denormalised
 * recipes, and the same-meal history read them all again on every opening of
 * « Remplacer » — 340 ms on a throttled phone. Bounded; a long tab keeps planning.
 */
const identityByContent = new Map<string, MealIdentity>();
const IDENTITY_BY_CONTENT_MAX = 4000;
const contentKey = (recipe: IdentifiableRecipe, ingredients: string[]): string =>
  `${recipe.id}\u0000${recipe.title}\u0000${recipe.translatedTitles?.en ?? ''}\u0000${recipe.translatedTitles?.fr ?? ''}\u0000${ingredients.length}\u0000${ingredients[0] ?? ''}\u0000${ingredients[ingredients.length - 1] ?? ''}`;

/**
 * A recipe's identity. Cached per recipe object: a generator scores the whole
 * library once per slot, and the regexes are the expensive part.
 */
export function mealIdentityOf(recipe: IdentifiableRecipe): MealIdentity {
  const hit = identityCache.get(recipe);
  if (hit) return hit;
  const byContent = identityByContent.get(contentKey(recipe, ingredientLinesOf(recipe)));
  if (byContent) {
    identityCache.set(recipe, byContent);
    return byContent;
  }
  const titles = [recipe.title, recipe.translatedTitles?.en, recipe.translatedTitles?.fr]
    .filter((t): t is string => typeof t === 'string' && t.trim() !== '');
  const ingredients = ingredientLinesOf(recipe);
  const identity: MealIdentity = {
    recipeId: String(recipe.id),
    titleKeys: [...new Set(titles.map(normalizeTitleKey).filter(Boolean))],
    format: dishFormatOf(recipe.title),
    protein: mainProteinOf(recipe.title, ingredients),
  };
  identityCache.set(recipe, identity);
  if (identityByContent.size >= IDENTITY_BY_CONTENT_MAX) identityByContent.clear();
  identityByContent.set(contentKey(recipe, ingredients), identity);
  return identity;
}

/** The strongest rule under which two dinners are the same meal, or null. */
export function sameMealRule(a: MealIdentity, b: MealIdentity): SameMealRule | null {
  if (a.recipeId === b.recipeId) return 'same-recipe';
  if (a.titleKeys.some((k) => b.titleKeys.includes(k))) return 'same-title';
  if (a.format && a.format === b.format) {
    return a.protein && a.protein === b.protein ? 'same-dish' : 'same-format';
  }
  return null;
}

/** Whole days between two calendar days, either order. */
function daysApart(a: string, b: string): number {
  return Math.abs(Math.round((parseIsoDate(a).getTime() - parseIsoDate(b).getTime()) / 86_400_000));
}

/** A dinner already on the calendar, or just chosen. */
export interface DatedMeal {
  date: string;
  identity: MealIdentity;
}

export interface SameMealConflict {
  rule: SameMealRule;
  with: DatedMeal;
}

/**
 * Every dinner the next pick has to keep its distance from: the past two
 * weeks, what is already planned around the slots being filled, and each pick
 * as it is made.
 */
export class SameMealLedger {
  private readonly meals: DatedMeal[];

  constructor(history: DatedMeal[] = []) {
    this.meals = [...history];
  }

  add(date: string, identity: MealIdentity): void {
    this.meals.push({ date, identity });
  }

  entries(): DatedMeal[] {
    return [...this.meals];
  }

  /** The first dinner `identity` on `date` would repeat, under the given rules. */
  conflict(
    identity: MealIdentity,
    date: string,
    rules: SameMealRule[] = SAME_MEAL_TIERS[0],
  ): SameMealConflict | null {
    for (const other of this.meals) {
      const rule = sameMealRule(identity, other.identity);
      if (!rule || !rules.includes(rule)) continue;
      if (daysApart(date, other.date) < SAME_MEAL_WINDOW_DAYS[rule]) return { rule, with: other };
    }
    return null;
  }

  /**
   * The candidates that repeat nothing, under the strictest tier that leaves
   * any. Order is preserved, so a ranked list stays ranked. Returns an empty
   * list only when even the last tier rules everything out — the same recipe
   * is never offered twice in two weeks to fill a slot.
   */
  allowed<T>(candidates: T[], identityOf: (c: T) => MealIdentity, date: string): { kept: T[]; tier: number } {
    for (let tier = 0; tier < SAME_MEAL_TIERS.length; tier++) {
      const kept = candidates.filter((c) => !this.conflict(identityOf(c), date, SAME_MEAL_TIERS[tier]));
      if (kept.length > 0) return { kept, tier };
    }
    return { kept: [], tier: SAME_MEAL_TIERS.length };
  }
}

/** A planned meal, as the history needs it. */
export interface HistoryMeal {
  scheduledFor: string;
  recipeId: string;
  recipe?: IdentifiableRecipe | null;
}

/**
 * The dated identities of planned meals. Meals without a recipe body (a
 * restaurant night, a note, leftovers) are not dishes and are left out.
 */
export function historyFromMeals(meals: HistoryMeal[]): DatedMeal[] {
  return meals.flatMap((m) => (m.recipe && m.recipe.title
    ? [{ date: m.scheduledFor, identity: mealIdentityOf({ ...m.recipe, id: m.recipeId }) }]
    : []));
}

/**
 * A suggestion filter for one slot: true for a recipe that would repeat one of
 * `meals` within the windows. `ignoreMealId` is the dish being swapped out — it
 * is leaving, so it is not something to keep away from.
 */
export function sameMealExcluder(
  meals: Array<HistoryMeal & { id?: string }>,
  date: string,
  ignoreMealId?: string,
): (recipe: IdentifiableRecipe) => boolean {
  const ledger = new SameMealLedger(historyFromMeals(meals.filter((m) => !ignoreMealId || m.id !== ignoreMealId)));
  return (recipe) => ledger.conflict(mealIdentityOf(recipe), date) !== null;
}
